import { FC, KeyboardEvent, PointerEvent, useCallback } from 'react'

import { useTriggerEvent } from 'hooks/useTriggerEvent'
import { useReferenceByMessageIdAndReferenceId } from 'pages/Conversation/hooks/useReferenceByMessageIdAndReferenceId'
import { useActiveReference, useOnClickInlineReference } from 'stores/useActiveMessageAndReferenceStore'
import { useOpenPanel } from 'stores/usePanelsStateStore'
import { getTrackingPropsForReferenceEvents } from 'utils/getTrackingPropsForReferenceEvents'

import { Tag } from './Tag'

type Props = {
  messageId?: string
  referenceId: string
  referenceNumber?: number
}

export const InlineReference: FC<Props> = ({
  messageId,
  referenceId,
  referenceNumber,
}) => {
  const triggerEvent = useTriggerEvent()

  const activeReference = useActiveReference()
  const onClickInlineReference = useOnClickInlineReference()

  const openPanel = useOpenPanel()

  const reference = useReferenceByMessageIdAndReferenceId(messageId, referenceId)

  const isActiveReference = activeReference?.referenceId === referenceId

  const handleTracking = useCallback(() => triggerEvent({
    name: 'kai_inline_reference_clicked',
    properties: getTrackingPropsForReferenceEvents({ reference }),
  }), [reference, triggerEvent])

  const onClick = useCallback(() => {
    if (!messageId) return

    openPanel('references')

    onClickInlineReference({ isActiveReference, messageId, referenceId })

    if (!isActiveReference) {
      handleTracking()
    }
  }, [handleTracking, isActiveReference, messageId, onClickInlineReference, openPanel, referenceId])

  const onClickHandler = useCallback((e: KeyboardEvent<HTMLButtonElement> | PointerEvent<HTMLButtonElement>) => {
    e.preventDefault()
    if ('code' in e && e.code !== 'Enter' && e.code !== 'Space') return
    onClick()
  }, [onClick])

  const renderInactiveReference = !reference || !messageId

  if (renderInactiveReference) {
    return (
      <span className="m-auto ml-1 inline-block h-6 min-w-6 cursor-default rounded-full bg-subdued p-1 text-center text-xs leading-4 no-underline">
        {referenceNumber}
      </span>
    )
  }

  return (
    <Tag
      reference={reference}
      messageId={messageId}
      referenceNumber={referenceNumber}
      isActiveReference={isActiveReference}
      onClick={onClickHandler}
    />
  )
}
